import { TFunction } from 'i18next';
import * as Yup from 'yup';
import {
    ApplyNowFormValues,
    RejectApplicationFormValues,
    ScheduleInterviewFormFields,
    SendEmailFormFields,
    QuestionAnswerFields,
    IApplyQuestion,
    QuestionType,
} from '../interfaces';
import moment, { Moment } from 'moment';

export const applyNowSchema = (t: TFunction, isEmployerPortal: boolean) => {
    return Yup.object().shape({
        [ApplyNowFormValues.firstName]: Yup.string().required(
            t('applyNow.firstNameRequired')
        ),
        [ApplyNowFormValues.lastName]: Yup.string().required(
            t('applyNow.lastNameRequired')
        ),
        [ApplyNowFormValues.email]: Yup.string()
            .email(t('applyNow.invalidEmail'))
            .required(t('applyNow.emailRequired')),
        [ApplyNowFormValues.phoneNumber]: Yup.string()
            .matches(/^05\d{8}$/, t('applyNow.invalidPhoneNumber'))
            .required(t('applyNow.phoneNumberRequired')),
        [ApplyNowFormValues.file]: Yup.mixed<File>()
            .nullable()
            .required(t('applyNow.resumeRequired'))
            .test('fileType', t('applyNow.invalidFileType'), (value) => {
                return value === null || value instanceof File;
            }),
        [ApplyNowFormValues.acceptTerms]: Yup.boolean().test(
            ApplyNowFormValues.acceptTerms,
            t('applyNow.acceptTermsIsRequired'),
            (val) => {
                return val === true;
            }
        ),
        [ApplyNowFormValues.isReferrer]: Yup.boolean().nullable(),
        [ApplyNowFormValues.referrerName]: Yup.string().when(
            ApplyNowFormValues.isReferrer,
            {
                is: true,
                then: (schema) =>
                    schema.required(t('applyNow.referrerNameRequired')),
                otherwise: (schema) => schema.nullable(),
            }
        ),
        [ApplyNowFormValues.referrerLastName]: Yup.string().when(
            ApplyNowFormValues.isReferrer,
            {
                is: true,
                then: (schema) =>
                    schema.required(t('applyNow.referrerLastNameRequired')),
                otherwise: (schema) => schema.nullable(),
            }
        ),
        [ApplyNowFormValues.referrerEmail]: Yup.string().when(
            ApplyNowFormValues.isReferrer,
            {
                is: true,
                then: (schema) =>
                    schema
                        .email(t('applyNow.invalidReferrerEmail'))
                        .required(t('applyNow.referrerEmailRequired')),
                otherwise: (schema) =>
                    schema.email(t('applyNow.invalidReferrerEmail')).nullable(),
            }
        ),
        [ApplyNowFormValues.referrerPhoneNumber]: Yup.string().when(
            ApplyNowFormValues.isReferrer,
            {
                is: true,
                then: (schema) =>
                    schema
                        .matches(
                            /^05\d{8}$/,
                            t('applyNow.invalidReferrerPhoneNumber')
                        )
                        .required(t('applyNow.referrerPhoneNumberRequired')),
                otherwise: (schema) =>
                    schema
                        .matches(
                            /^05\d{8}$/,
                            t('applyNow.invalidReferrerPhoneNumber')
                        )
                        .nullable(),
            }
        ),
        [ApplyNowFormValues.referrerCompany]: Yup.string().when(
            ApplyNowFormValues.isReferrer,
            {
                is: true,
                then: (schema) =>
                    schema.required(t('applyNow.referrerCompanyRequired')),
                otherwise: (schema) => schema.nullable(),
            }
        ),
        [ApplyNowFormValues.coverLetter]: Yup.string().nullable(),
        [ApplyNowFormValues.jobApplicationId]: isEmployerPortal
            ? Yup.object()
                  .shape({
                      value: Yup.number().required(),
                      label: Yup.string().required(),
                  })
                  .nullable()
            : Yup.number().nullable(),
    });
};

export const applyNowCandidateInformationSchema = (t: TFunction, isEmployerPortal: boolean) => {
  return Yup.object().shape({
    [ApplyNowFormValues.firstName]: Yup.string().required(
      t("applyNow.firstNameRequired")
    ),
    [ApplyNowFormValues.lastName]: Yup.string().required(
      t("applyNow.lastNameRequired")
    ),
    [ApplyNowFormValues.email]: Yup.string()
      .email(t("applyNow.invalidEmail"))
      .required(t("applyNow.emailRequired")),
    [ApplyNowFormValues.phoneNumber]: Yup.string()
      .matches(/^05\d{8}$/, t("applyNow.invalidPhoneNumber"))
      .required(t("applyNow.phoneNumberRequired")),
    [ApplyNowFormValues.file]: Yup.mixed<File>()
      .nullable()
      .required(t("applyNow.resumeRequired"))
      .test("fileType", t("applyNow.invalidFileType"), (value) => {
        return value === null || value instanceof File;
      }),
    [ApplyNowFormValues.jobApplicationId]: isEmployerPortal
      ? Yup.object()
          .shape({
            value: Yup.number().required(
              t("applyNow.jobApplicationIdRequired")
            ),
            label: Yup.string().required(
              t("applyNow.jobApplicationIdRequired")
            ),
          })
          .nullable()
      : Yup.object().nullable(),
    [ApplyNowFormValues.isReferrer]: Yup.boolean().nullable(),
  });
};

export const applyNowReferrerInformationSchema = (t: TFunction) => {
  return Yup.object().shape({
    [ApplyNowFormValues.referrerName]: Yup.string().required(
      t("applyNow.referrerNameRequired")
    ),
    [ApplyNowFormValues.referrerLastName]: Yup.string().required(
      t("applyNow.referrerLastNameRequired")
    ),
    [ApplyNowFormValues.referrerEmail]: Yup.string()
      .email(t("applyNow.invalidReferrerEmail"))
      .required(t("applyNow.referrerEmailRequired")),
    [ApplyNowFormValues.referrerPhoneNumber]: Yup.string()
      .matches(/^05\d{8}$/, t("applyNow.invalidReferrerPhoneNumber"))
      .required(t("applyNow.referrerPhoneNumberRequired")),
    [ApplyNowFormValues.referrerCompany]: Yup.string().required(
      t("applyNow.referrerCompanyRequired")
    ),
    [ApplyNowFormValues.coverLetter]: Yup.string().nullable(),
  });
};

export const applyNowAdditionalQuestionsSchema = (
    t: TFunction,
    applyQuestions: Array<IApplyQuestion> = []
) => {
    return Yup.object().shape({
        [ApplyNowFormValues.acceptTerms]: Yup.boolean().test(
            ApplyNowFormValues.acceptTerms,
            t('applyNow.acceptTermsIsRequired'),
            (val) => {
                return val === true;
            }
        ),
        [ApplyNowFormValues.questionAnswers]: Yup.array().of(
            Yup.object().shape({
                [QuestionAnswerFields.QUESTION_ID]: Yup.number().required(),
                [QuestionAnswerFields.ANSWER]: Yup.mixed()
                    .nullable()
                    .when(QuestionAnswerFields.QUESTION_ID, ([questionId], schema) => {
                        const question = applyQuestions.find(
                            (q) => q.id === questionId
                        );
                        if (!question?.isRequired) return schema;

                        switch (question.type) {
                            case QuestionType.SINGLE_SELECT:
                                return schema.test(
                                    'hasSelection',
                                    t('applyNow.questionRequired'),
                                    (val) =>
                                        typeof val === 'string' && val !== ''
                                );
                            case QuestionType.MULTIPLE_SELECT:
                                return schema.test(
                                    'hasSelection',
                                    t('applyNow.questionRequired'),
                                    (val) =>
                                        Array.isArray(val) && val.length > 0
                                );
                            case QuestionType.FILE_UPLOAD:
                                return schema.test(
                                    'hasFile',
                                    t('applyNow.questionRequired'),
                                    (val) =>
                                        val !== null && val !== undefined
                                );
                            case QuestionType.DATE_RANGE:
                                return schema.test(
                                    'hasDateRange',
                                    t('applyNow.questionRequired'),
                                    (val: any) => val?.from && val?.to
                                );
                            default:
                                return schema.test(
                                    'hasValue',
                                    t('applyNow.questionRequired'),
                                    (val) =>
                                        val !== '' &&
                                        val !== null &&
                                        val !== undefined
                                );
                        }
                    }),
            })
        ),
    });
};

export const rejectApplicationSchema = () => {
    return Yup.object().shape({
        [RejectApplicationFormValues.reasonOption]: Yup.object()
            .shape({
                value: Yup.string().required(),
                label: Yup.string().required(),
            })
            .nullable()
            .default(null)
            .test('reason-required', '', function (value) {
                const { reasonText } = this.parent;
                const isOptionValid = value && value.value;
                const isTextValid = reasonText && reasonText.trim() !== '';
                return isOptionValid || isTextValid;
            }),
        [RejectApplicationFormValues.reasonText]: Yup.string()
            .nullable()
            .default(null)
            .test('reason-required', '', function (value) {
                const { reasonOption } = this.parent;
                const isOptionValid = reasonOption && reasonOption.value;
                const isTextValid = value && value.trim() !== '';
                return isOptionValid || isTextValid;
            }),
    });
};

export const scheduleInterviewSchema = (t: TFunction) => {
    return Yup.object().shape({
        [ScheduleInterviewFormFields.date]: Yup.mixed<Moment>()
            .nullable()
            .required(t('jobApplicationPage.dateRequired')),
        [ScheduleInterviewFormFields.name]: Yup.string().required(
            t('jobApplicationPage.interviewNameRequired')
        ),
        [ScheduleInterviewFormFields.timeFrom]: Yup.mixed<Moment>()
            .nullable()
            .required(t('jobApplicationPage.timeFromRequired'))
            .test(
                'is-valid-time',
                t('jobApplicationPage.invalidTimeFormat'),
                (value) => {
                    return value === null || moment.isMoment(value);
                }
            ),
        [ScheduleInterviewFormFields.timeTo]: Yup.mixed<Moment>()
            .nullable()
            .required(t('jobApplicationPage.timeToRequired'))
            .test(
                'is-valid-time',
                t('jobApplicationPage.invalidTimeFormat'),
                (value) => {
                    return value === null || moment.isMoment(value);
                }
            )
            .test(
                'is-after',
                t('jobApplicationPage.timeToAfterTimeFrom'),
                function (value) {
                    const { timeFrom } = this.parent;
                    if (!timeFrom || !value) return true;
                    if (!moment.isMoment(timeFrom) || !moment.isMoment(value))
                        return true;
                    return value.isAfter(timeFrom);
                }
            ),
        [ScheduleInterviewFormFields.description]: Yup.string()
            .nullable()
            .default(''),
    });
};

export const sendEmailSchema = (t: TFunction) => {
    return Yup.object().shape({
        [SendEmailFormFields.subject]: Yup.string().required(
            t('jobApplicationPage.subjectRequired')
        ),
        [SendEmailFormFields.body]: Yup.string()
            .test(
                'not-empty-html',
                t('jobApplicationPage.bodyRequired'),
                (value) => {
                    if (!value) return false;
                    const text = value.replace(/<[^>]*>/g, '').trim();
                    return text.length > 0;
                }
            ),
    });
};
